import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { withLogger } from '@/new-transport/_store/logger';
import { withBasicTransport } from '@/new-transport/_store/basic-transport.feature';
import { withLoadingFeature } from '@/_signal-store-features/loading.feature.store';
import {
	CoachListData,
	CoachSlotGroupItem,
	CouchGroupByVehicles,
	VehicleLayout,
} from '@/new-transport/_models/transport-coach-list.model';
import { tapResponse } from '@ngrx/operators';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { EMPTY, filter, pipe, switchMap, take } from 'rxjs';
import { TransportCoachListApiService } from '../_api/transport-coach-list.api.service';
import { computed, inject } from '@angular/core';
import { groupCouchListByVehicles } from '@/new-transport/_utils/group-couch-list-by-vehicles/group-couch-list-by-vehicles';
import { SeatStatus } from '@oskar-frontend/comfort-package';
import { toObservable } from '@angular/core/rxjs-interop';
import { updateCoachListWithSeat } from '@/new-transport/_utils/update-coach-list-with-seat/update-coach-list-with-seat';
import { mapCoachListToCreatePayload } from '@/new-transport/_utils/map-coach-list-to-create-payload/map-coach-list-to-create-payload';
import { InfoboxService } from '@/common/infobox/infobox.service';
import { MatDialog } from '@angular/material/dialog';
import {
	CoachListConfirmationComponent,
	CoachListConfirmationDialogData,
} from '../coach-list-confirmation/coach-list-confirmation.component';

export type TransportCoachListState = {
	coachList: CoachListData[];
	layouts: VehicleLayout[];
};

const transportCoachListInitialState: TransportCoachListState = {
	coachList: [],
	layouts: [],
};

export const TransportCoachListStore = signalStore(
	withState(transportCoachListInitialState),
	withLogger('coach-list'),
	withLoadingFeature({ coachListIsLoading: false, layoutIsLoading: false }),
	withBasicTransport(),
	withComputed(({ coachList, layouts, transport, transportsToLinkNames }) => ({
		couchGroupedByVehicle: computed(() => {
			const groupedCoachList = groupCouchListByVehicles(
				coachList(),
				layouts(),
				transport()?.side,
				transportsToLinkNames(),
			);

			groupedCoachList.forEach((coach) => {
				coach?.layout?.seats.forEach((seat) => {
					if (seat.kind === SeatStatus.SEAT || seat.kind === SeatStatus.EMERGENCY) {
						const isSeatTaken = coach.slots.some((slot) => slot.slotVehicle.seat.number === seat.number);

						seat.kind = isSeatTaken
							? SeatStatus.SEAT
							: seat.kind === SeatStatus.EMERGENCY
								? SeatStatus.EMERGENCY
								: SeatStatus.AVAILABLE;
					}
				});
			});

			return groupedCoachList;
		}),
	})),
	withMethods(
		(
			store,
			apiService = inject(TransportCoachListApiService),
			infoboxService = inject(InfoboxService),
			dialog = inject(MatDialog),
		) => ({
			updateSlotSeat(selectedSlot: CoachSlotGroupItem, seatId: number, couchByVehicle: CouchGroupByVehicles) {
				patchState(store, (state) => {
					const layout = store.layouts().find((l) => l.id === couchByVehicle.layout.id);
					if (!layout) {
						return state;
					}

					const updatedCoachList = updateCoachListWithSeat(
						state.coachList,
						selectedSlot,
						selectedSlot.slotVehicle,
						layout,
						seatId,
						couchByVehicle.vehicle,
						state.transport.side,
					);

					return {
						...state,
						coachList: updatedCoachList,
					};
				});
			},
			refreshLayouts() {
				patchState(store, (state) => {
					return { ...state, layouts: [...state.layouts] };
				});
			},
			removeSlotSeat(coach: CoachSlotGroupItem): void {
				patchState(store, (state) => ({
					coachList: state.coachList.map((c) =>
						c.id !== coach.id
							? c
							: {
									...c,
									slotVehicles: c.slotVehicles.map((slotVehicle) =>
										slotVehicle.id === coach.slotVehicle.id &&
										slotVehicle.order === coach.slotVehicle.order
											? {
													...slotVehicle,
													seat: {
														...slotVehicle.seat,
														number: null,
													},
												}
											: slotVehicle,
									),
								},
					),
				}));
			},
			fetchCoachList: rxMethod<{ transportId: number; reassignAll: boolean; copyFromDeparture: boolean }>(
				pipe(
					switchMap(({ transportId, reassignAll, copyFromDeparture }) =>
						apiService.getCoachList(transportId, reassignAll, copyFromDeparture).pipe(
							tapResponse({
								next: (coachList) => {
									patchState(store, () => ({
										coachList,
									}));
								},
								error: () => {
									throw new Error('Wystąpił problem przy pobieraniu list autokarowych');
								},
								finalize: () => {
									store.changeLoadingState('coachList', false);
								},
							}),
						),
					),
				),
			),
			fetchLayoutList: rxMethod<void>(
				pipe(
					switchMap(() =>
						apiService.getLayouts().pipe(
							tapResponse({
								next: (result) => {
									patchState(store, () => ({
										layouts: result.results,
									}));
								},
								error: () => {
									throw new Error('Wystąpił problem przy pobieraniu layoutów list autokarowych');
								},
								finalize: () => {
									store.changeLoadingState('layout', false);
								},
							}),
						),
					),
				),
			),
			updateCoachList: rxMethod<{ payload: CoachListData[] }>(
				pipe(
					switchMap(({ payload }) => {
						store.changeLoadingState('coachList', true);

						return apiService.updateCoachList(mapCoachListToCreatePayload(payload)).pipe(
							switchMap(() => {
								console.log('Success on save coach list');

								const transportId = store.transport()?.id;
								if (!transportId) {
									return EMPTY;
								}

								if (store.transport().coachListUrl) {
									return dialog
										.open<CoachListConfirmationComponent, CoachListConfirmationDialogData>(
											CoachListConfirmationComponent,
											{
												maxWidth: '480px',
												data: {
													title: 'Generowanie list',
													message: `Czy po zapisie chcesz wygenerować na nowo listy autokarowe? </br> 
                        Aktualną listę znajdziesz pod linkiem - 
                        <a href="${store.transport().coachListUrl}" target="_blank" rel="noopener noreferrer">
                          ${store.transport().coachListUrl}
                        </a>`,
												},
											},
										)
										.afterClosed()
										.pipe(
											filter(Boolean),
											switchMap(() => apiService.generateTransportList(transportId)),
										);
								} else {
									return apiService.generateTransportList(transportId);
								}
							}),
							tapResponse({
								next: (coachListSheetURL) => {
									window.open(coachListSheetURL, '_blank');
									store.fetchTransport(store.transport().id);
									infoboxService.success('Pomyślnie wygenerowano i zapisano listy autokarowe');
								},
								error: () => {
									throw new Error('Wystąpił problem przy zapisie list autokarowych');
								},
								finalize: () => {
									store.changeLoadingState('coachList', false);
								},
							}),
						);
					}),
				),
			),
		}),
	),
	withHooks({
		onInit({ fetchCoachList, fetchLayoutList, changeLoadingState, transport }) {
			toObservable(transport)
				.pipe(filter(Boolean), take(1))
				.subscribe((transport) => {
					changeLoadingState('coachList', true);
					fetchCoachList({ transportId: transport?.id, reassignAll: false, copyFromDeparture: false });
				});
			changeLoadingState('layout', true);
			fetchLayoutList();
		},
	}),
);
